import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, FeedbackOutlined, MenuBook, SmartToyOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/api';

const SETUP_GUIDE_PATH = '/provider/tutorials?topic=ai-assistant';

const KNOWN_ASSISTANTS = ['ChatGPT', 'Muse', 'Claude'];
const OTHER = 'Other';

/** Splits a stored assistant into the dropdown choice plus the "Other" text. */
const assistantChoice = (assistant: string | null | undefined): { choice: string; other: string } => {
  if (!assistant) return { choice: '', other: '' };
  return KNOWN_ASSISTANTS.includes(assistant) ? { choice: assistant, other: '' } : { choice: OTHER, other: assistant };
};

const connectionLabel = (c: { name: string; assistant: string | null }): string => {
  if (!c.assistant) return c.name;
  return c.assistant.toLowerCase() === c.name.toLowerCase() ? c.assistant : `${c.assistant} (${c.name})`;
};

const FEEDBACK_KINDS: Array<{ value: 'worked' | 'problem' | 'idea'; label: string }> = [
  { value: 'worked', label: 'Something worked well' },
  { value: 'problem', label: 'Something went wrong' },
  { value: 'idea', label: 'Idea or request' },
];

interface ScopeDef {
  scope: string;
  label: string;
  description: string;
  access: 'read' | 'write';
  available: boolean;
}

interface AgentStatus {
  eligible: boolean;
  scopes?: ScopeDef[];
  changesEnabled?: boolean;
  apiUrl?: string;
  maxConnections?: number;
  termsVersion?: string;
}

interface AgentConnection {
  id: string;
  name: string;
  assistant: string | null;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  rotatedAt: string | null;
  revokedAt: string | null;
  termsAcceptedAt: string | null;
  termsVersion: string | null;
}

interface ActivityEntry {
  id: string;
  action: string;
  outcome: 'success' | 'error' | 'denied' | 'rate_limited';
  createdAt: string;
  errorMessage: string | null;
  input: { summary?: string } | null;
}

const CHANGE_LABELS: Record<string, string> = {
  move_day: 'move a day',
  move_visits: 'move visits',
  schedule_service_for_day: 'schedule a service for a day',
  skip_visit: 'skip a visit',
  add_customer: 'add a customer',
};

const ACTION_LABELS: Record<string, string> = {
  get_connection_info: 'Connected and checked its permissions',
  get_route_status: 'Checked route progress',
  get_schedule: 'Looked up the schedule',
  find_customer: 'Searched customers',
  list_customers: 'Listed customers',
  get_agreements_status: 'Checked agreements',
  get_renewals_status: 'Checked renewals',
  get_revenue_forecast: 'Checked the revenue forecast',
  get_estimated_payout: 'Checked the estimated payout',
  get_property_profit: 'Checked property profit',
  auth: 'Tried to connect',
  'connection.created': 'Connection created',
  'connection.updated': 'Permissions or name changed',
  'connection.rotated': 'New key issued',
  'connection.revoked': 'Access revoked',
};

const activityLabel = (action: string): string => {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const [tool, step] = action.split('.');
  const change = CHANGE_LABELS[tool];
  if (!change) return action;
  if (step === 'confirmed') return `Confirmed: ${change}`;
  if (step === 'cancelled') return `Cancelled: ${change}`;
  return `Previewed: ${change}`;
};

const OUTCOME_LABELS: Record<ActivityEntry['outcome'], { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
  success: { label: 'OK', color: 'success' },
  error: { label: 'Failed', color: 'error' },
  denied: { label: 'Not allowed', color: 'warning' },
  rate_limited: { label: 'Too many requests', color: 'warning' },
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

const CopyField: React.FC<{ label: string; value: string; monospace?: boolean }> = ({ label, value, monospace }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      InputProps={{
        readOnly: true,
        sx: monospace ? { fontFamily: 'monospace', fontSize: '0.85rem' } : undefined,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton aria-label={`Copy ${label}`} onClick={copy} edge="end" size="small">
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
};

const ScopeCheckboxes: React.FC<{ defs: ScopeDef[]; value: string[]; onChange: (scopes: string[]) => void }> = ({
  defs,
  value,
  onChange,
}) => (
  <FormGroup>
    {defs.filter((d) => d.available).map((d) => (
      <FormControlLabel
        key={d.scope}
        control={
          <Checkbox
            checked={value.includes(d.scope)}
            onChange={(e) => onChange(e.target.checked ? [...value, d.scope] : value.filter((s) => s !== d.scope))}
          />
        }
        label={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {d.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {d.description}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', mb: 1, '& .MuiCheckbox-root': { pt: 0.5 } }}
      />
    ))}
  </FormGroup>
);

const ConnectedAgentsCard: React.FC = () => {
  const [status, setStatus] = useState<AgentStatus | null>(null);
  const [connections, setConnections] = useState<AgentConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; connection?: AgentConnection } | null>(null);
  const [formName, setFormName] = useState('');
  const [formAssistant, setFormAssistant] = useState('');
  const [formAssistantOther, setFormAssistantOther] = useState('');
  const [formScopes, setFormScopes] = useState<string[]>([]);
  const [formAcceptTerms, setFormAcceptTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const [issuedKey, setIssuedKey] = useState<{ name: string; key: string } | null>(null);
  const [confirming, setConfirming] = useState<{ type: 'revoke' | 'rotate'; connection: AgentConnection } | null>(null);
  const [activityFor, setActivityFor] = useState<AgentConnection | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[] | null>(null);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackKind, setFeedbackKind] = useState<'worked' | 'problem' | 'idea'>('worked');
  /** "key:<connectionId>", "assistant:<name>", "other", or "" for not about one assistant. */
  const [feedbackAbout, setFeedbackAbout] = useState('');
  const [feedbackOther, setFeedbackOther] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const scopeDefs = status?.scopes || [];
  const readDefs = scopeDefs.filter((d) => d.access === 'read');
  const writeDefs = scopeDefs.filter((d) => d.access === 'write');
  const changesEnabled = !!status?.changesEnabled;
  const scopeLabel = (scope: string) => scopeDefs.find((d) => d.scope === scope)?.label || scope;
  const isWrite = (scope: string) => scopeDefs.find((d) => d.scope === scope)?.access === 'write';
  const active = connections.filter((c) => !c.revokedAt);
  const termsCurrent = (c: AgentConnection) => !!c.termsAcceptedAt && c.termsVersion === status?.termsVersion;
  const revoked = connections.filter((c) => c.revokedAt).slice(0, 3);

  const load = useCallback(async () => {
    try {
      const s = await apiService.get<AgentStatus>('/agent-connections/status');
      const next = (s.data as AgentStatus) || { eligible: false };
      setStatus(next);
      if (next.eligible) {
        const list = await apiService.get<AgentConnection[]>('/agent-connections');
        setConnections((list.data as AgentConnection[]) || []);
      }
    } catch {
      setStatus({ eligible: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !status?.eligible) return null;

  const openCreate = () => {
    setFormName('');
    setFormAssistant('');
    setFormAssistantOther('');
    setFormScopes(readDefs.filter((d) => d.available).map((d) => d.scope));
    setFormAcceptTerms(false);
    setEditing({ mode: 'create' });
  };

  const openEdit = (connection: AgentConnection) => {
    const { choice, other } = assistantChoice(connection.assistant);
    setFormName(connection.name);
    setFormAssistant(choice);
    setFormAssistantOther(other);
    setFormScopes(connection.scopes);
    setFormAcceptTerms(false);
    setEditing({ mode: 'edit', connection });
  };

  const needsTerms = !!editing && (editing.mode === 'create' || (!!editing.connection && !termsCurrent(editing.connection)));

  const formAssistantValue = formAssistant === OTHER ? formAssistantOther.trim() : formAssistant;

  /** Keeps the name in step with the assistant until the provider types their own name. */
  const chooseAssistant = (choice: string) => {
    const previous = formAssistant === OTHER ? '' : formAssistant;
    if (!formName.trim() || formName === previous) setFormName(choice === OTHER ? '' : choice);
    setFormAssistant(choice);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      if (editing.mode === 'create') {
        const res = await apiService.post<{ connection: AgentConnection; key: string }>('/agent-connections', {
          name: formName.trim(),
          assistant: formAssistantValue,
          scopes: formScopes,
          acceptTerms: formAcceptTerms,
        });
        const data = res.data as { connection: AgentConnection; key: string };
        setIssuedKey({ name: data.connection.name, key: data.key });
      } else if (editing.connection) {
        await apiService.patch(`/agent-connections/${editing.connection.id}`, {
          name: formName.trim(),
          assistant: formAssistantValue,
          scopes: formScopes,
          ...(needsTerms && formAcceptTerms ? { acceptTerms: true } : {}),
        });
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not save the connection.');
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = async () => {
    if (!confirming) return;
    const { type, connection } = confirming;
    setSaving(true);
    setError('');
    try {
      if (type === 'rotate') {
        const res = await apiService.post<{ connection: AgentConnection; key: string }>(`/agent-connections/${connection.id}/rotate`);
        const data = res.data as { connection: AgentConnection; key: string };
        setIssuedKey({ name: data.connection.name, key: data.key });
      } else {
        await apiService.post(`/agent-connections/${connection.id}/revoke`);
      }
      setConfirming(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const openActivity = async (connection: AgentConnection) => {
    setActivityFor(connection);
    setActivity(null);
    try {
      const res = await apiService.get<ActivityEntry[]>(`/agent-connections/${connection.id}/activity`);
      setActivity((res.data as ActivityEntry[]) || []);
    } catch {
      setActivity([]);
    }
  };

  const openFeedback = () => {
    setFeedbackKind('worked');
    setFeedbackAbout(active.length === 1 ? `key:${active[0].id}` : '');
    setFeedbackOther('');
    setFeedbackMessage('');
    setFeedbackError('');
    setFeedbackOpen(true);
  };

  const sendFeedback = async () => {
    setSaving(true);
    setFeedbackError('');
    try {
      await apiService.post('/agent-connections/feedback', {
        kind: feedbackKind,
        message: feedbackMessage.trim(),
        connectionId: feedbackAbout.startsWith('key:') ? feedbackAbout.slice(4) : '',
        assistant: feedbackAbout.startsWith('assistant:')
          ? feedbackAbout.slice(10)
          : feedbackAbout === 'other'
            ? feedbackOther.trim()
            : '',
      });
      setFeedbackOpen(false);
      setFeedbackSent(true);
    } catch (e: any) {
      setFeedbackError(e?.message || 'Could not send your feedback. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openApiUrl = status.apiUrl ? `${status.apiUrl}/openapi.json` : '';

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Connected AI Agents
            <Chip label="Pilot" size="small" color="warning" variant="outlined" />
          </Box>
        }
        avatar={<SmartToyOutlined />}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {changesEnabled ? (
            <>
              Let your own AI assistant (ChatGPT, Muse, or another agent that can connect to outside apps) answer questions about your schedule, routes,
              customers, and numbers — and, only if you allow it, move or skip visits and add customers. Every change is shown
              to you first and runs only after you say yes. Agents can’t move money, change prices, or cancel services.
              LawnHub doesn’t charge for this and doesn’t run an AI model itself.
            </>
          ) : (
            <>
              Let your own AI assistant (ChatGPT, Muse, or another agent that can connect to outside apps) answer questions about your schedule, routes,
              customers, and numbers. Connected agents can only <strong>read</strong> — they can’t change anything, move money,
              or contact customers. LawnHub doesn’t charge for this and doesn’t run an AI model itself.
            </>
          )}
        </Typography>

        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>This is a pilot.</strong> It works on your real account, and we’re still learning how providers use it.
            Tell us what works, what doesn’t, and what you’d like it to do.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<MenuBook />} component={RouterLink} to={SETUP_GUIDE_PATH}>
              Setup guide
            </Button>
            <Button size="small" variant="outlined" startIcon={<FeedbackOutlined />} onClick={openFeedback}>
              Send feedback
            </Button>
          </Stack>
        </Alert>

        {feedbackSent && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFeedbackSent(false)}>
            Thanks — your feedback was sent to the LawnHub team.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {active.length === 0 ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            No agents connected yet.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
            {active.map((c) => (
              <Box key={c.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{c.name}</Typography>
                    {c.assistant ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Assistant: {c.assistant}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        Assistant: not set —{' '}
                        <Box
                          component="button"
                          type="button"
                          onClick={() => openEdit(c)}
                          sx={{ p: 0, border: 0, background: 'none', color: 'primary.main', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
                        >
                          choose it
                        </Box>
                      </Typography>
                    )}
                    {!termsCurrent(c) && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        Updated Connected AI Agents terms —{' '}
                        <Box
                          component="button"
                          type="button"
                          onClick={() => openEdit(c)}
                          sx={{ p: 0, border: 0, background: 'none', color: 'primary.main', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
                        >
                          review and agree
                        </Box>
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {c.keyPrefix}…
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {c.lastUsedAt ? `Last used ${formatDateTime(c.lastUsedAt)}` : 'Not used yet'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Button size="small" onClick={() => openActivity(c)}>
                      Activity
                    </Button>
                    <Button size="small" onClick={() => openEdit(c)}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => setConfirming({ type: 'rotate', connection: c })}>
                      New key
                    </Button>
                    <Button size="small" color="error" onClick={() => setConfirming({ type: 'revoke', connection: c })}>
                      Revoke
                    </Button>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {c.scopes.map((s) => (
                    <Chip key={s} label={scopeLabel(s)} size="small" variant="outlined" color={isWrite(s) ? 'warning' : 'default'} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}

        {revoked.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Recently revoked: {revoked.map((c) => `${c.name} (${formatDateTime(c.revokedAt)})`).join(', ')}
          </Typography>
        )}

        <Button
          variant="outlined"
          startIcon={<SmartToyOutlined />}
          onClick={openCreate}
          disabled={active.length >= (status.maxConnections || 5)}
        >
          Connect an agent
        </Button>
      </CardContent>

      {/* Create / edit */}
      <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.mode === 'create' ? 'Connect an agent' : 'Edit agent'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Which AI assistant?"
            value={formAssistant}
            onChange={(e) => chooseAssistant(e.target.value)}
            fullWidth
            margin="normal"
            required
          >
            {KNOWN_ASSISTANTS.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
            <MenuItem value={OTHER}>Other…</MenuItem>
          </TextField>
          {formAssistant === OTHER && (
            <TextField
              label="Assistant name"
              placeholder="e.g. Gemini, Copilot"
              value={formAssistantOther}
              onChange={(e) => setFormAssistantOther(e.target.value)}
              fullWidth
              margin="dense"
              required
              inputProps={{ maxLength: 40 }}
            />
          )}
          <TextField
            label="Name"
            helperText="So you can tell your agents apart, e.g. “Muse – office” or “ChatGPT – phone”."
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 60 }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            What this agent can see
          </Typography>
          <ScopeCheckboxes defs={readDefs} value={formScopes} onChange={setFormScopes} />
          {changesEnabled && writeDefs.some((d) => d.available) && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                What this agent can change
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Off unless you turn it on. The agent shows you a preview of every change and only goes ahead after you say yes.
              </Typography>
              <ScopeCheckboxes defs={writeDefs} value={formScopes} onChange={setFormScopes} />
            </>
          )}
          {needsTerms && (
            <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'warning.light', borderRadius: 1 }}>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', m: 0 }}
                control={
                  <Checkbox
                    checked={formAcceptTerms}
                    onChange={(e) => setFormAcceptTerms(e.target.checked)}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the Connected AI Agents terms in the{' '}
                    <Box component={RouterLink} to="/legal" target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
                      Provider Service Agreement (§14.1.4)
                    </Box>
                    . My AI assistant is my choice and my responsibility: I take full responsibility and liability for
                    everything it does with my account, LawnHub is not responsible for its actions, and LawnHub’s help is
                    limited to the setup guides — LawnHub does not support my assistant itself.
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={
              saving || !formAssistantValue || !formName.trim() || formScopes.length === 0 || (needsTerms && !formAcceptTerms)
            }
          >
            {saving ? <CircularProgress size={20} /> : editing?.mode === 'create' ? 'Create key' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Key shown once */}
      <Dialog open={!!issuedKey} maxWidth="sm" fullWidth>
        <DialogTitle>Your key for {issuedKey?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. For your security, LawnHub won’t show it again — if you lose it, issue a new key.
          </Alert>
          <Stack spacing={2}>
            <CopyField label="Agent key" value={issuedKey?.key || ''} monospace />
            {status.apiUrl && <CopyField label="API address" value={status.apiUrl} />}
            {openApiUrl && <CopyField label="OpenAPI document" value={openApiUrl} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            In your assistant’s settings, add LawnHub as a tool or action using the OpenAPI document, then paste the agent key
            as a Bearer token (API key). Treat the key like a password.{' '}
            <Box component={RouterLink} to={SETUP_GUIDE_PATH} target="_blank" rel="noopener" sx={{ color: 'primary.main' }}>
              Step-by-step setup guide
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIssuedKey(null)}>
            I’ve copied it
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke / rotate confirmation */}
      <Dialog open={!!confirming} onClose={() => !saving && setConfirming(null)}>
        <DialogTitle>{confirming?.type === 'revoke' ? `Revoke ${confirming?.connection.name}?` : 'Issue a new key?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirming?.type === 'revoke'
              ? 'The agent loses access immediately. You can connect it again later with a new key.'
              : 'The current key stops working immediately. You’ll need to paste the new key into your assistant.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirming?.type === 'revoke' ? 'error' : 'primary'}
            onClick={confirmAction}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : confirming?.type === 'revoke' ? 'Revoke access' : 'Issue new key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pilot feedback */}
      <Dialog open={feedbackOpen} onClose={() => !saving && setFeedbackOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send feedback about AI agents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Goes straight to the LawnHub team. If something went wrong, include what you asked your assistant and what it did.
          </Typography>
          {feedbackError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {feedbackError}
            </Alert>
          )}
          <RadioGroup value={feedbackKind} onChange={(e) => setFeedbackKind(e.target.value as typeof feedbackKind)}>
            {FEEDBACK_KINDS.map((k) => (
              <FormControlLabel key={k.value} value={k.value} control={<Radio size="small" />} label={k.label} />
            ))}
          </RadioGroup>
          <TextField
            select
            label="Which assistant? (optional)"
            value={feedbackAbout}
            onChange={(e) => setFeedbackAbout(e.target.value)}
            fullWidth
            margin="normal"
            size="small"
          >
            <MenuItem value="">Not about a specific assistant</MenuItem>
            {active.length > 0 && <ListSubheader>Your connected agents</ListSubheader>}
            {active.map((c) => (
              <MenuItem key={c.id} value={`key:${c.id}`}>
                {connectionLabel(c)}
              </MenuItem>
            ))}
            <ListSubheader>Other assistants</ListSubheader>
            {KNOWN_ASSISTANTS.map((a) => (
              <MenuItem key={a} value={`assistant:${a}`}>
                {a}
              </MenuItem>
            ))}
            <MenuItem value="other">Other…</MenuItem>
          </TextField>
          {feedbackAbout === 'other' && (
            <TextField
              label="Assistant name"
              placeholder="e.g. Gemini, Copilot"
              value={feedbackOther}
              onChange={(e) => setFeedbackOther(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
              inputProps={{ maxLength: 60 }}
            />
          )}
          <TextField
            label="Your feedback"
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            margin="normal"
            inputProps={{ maxLength: 2000 }}
            helperText={`${feedbackMessage.length}/2000`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={sendFeedback} disabled={saving || feedbackMessage.trim().length < 3}>
            {saving ? <CircularProgress size={20} /> : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity */}
      <Dialog open={!!activityFor} onClose={() => setActivityFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{activityFor?.name} — recent activity</DialogTitle>
        <DialogContent dividers>
          {activity === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activity.length === 0 ? (
            <Typography variant="body2">No activity yet.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {activity.map((e) => (
                <Box key={e.id} sx={{ py: 1.25, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{activityLabel(e.action)}</Typography>
                    {e.input?.summary && (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        {e.input.summary}
                      </Typography>
                    )}
                    {e.outcome !== 'success' && e.errorMessage && (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        {e.errorMessage}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(e.createdAt)}
                    </Typography>
                  </Box>
                  <Chip
                    label={OUTCOME_LABELS[e.outcome]?.label || e.outcome}
                    color={OUTCOME_LABELS[e.outcome]?.color || 'default'}
                    size="small"
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ConnectedAgentsCard;
